import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Server-side environment variables schema.
   * These are only available on the server and should never be exposed to the client.
   */
  server: {
    NODE_ENV: z.enum(["development", "test", "production"]),
    STRIPE_SECRET_KEY: z.string(),
    STRIPE_API_METERS: z.string(), // JSON array of meter configs
    UNKEY_ROOT_KEY: z.string(),
    UNKEY_DEV_API_ID: z.string(),
    STRIPE_SUBSCRIPTION_PLANS: z.string().optional(), // JSON array of {id, priceId, name, amount}
  },

  /**
   * Client-side environment variables schema.
   * These are exposed to the client via the `NEXT_PUBLIC_` prefix.
   */
  client: {
    NEXT_PUBLIC_SUPABASE_URL: z.string(),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
    NEXT_PUBLIC_SITU_BASE_URL: z.string().optional(),
    NEXT_PUBLIC_HELPSCOUT_BEACON_ID: z.string().optional(),
    NEXT_PUBLIC_NPM_TOKEN: z.string().optional(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_VERCEL_ENV: z
      .enum(["production", "preview", "development"])
      .optional(),
    NEXT_PUBLIC_GRAFANA_BASE_URL: z.string(),
    NEXT_PUBLIC_TRACES_API_BASE_URL: z.string(),
    NEXT_PUBLIC_ENABLE_LOGIN: z
      .enum(["true", "false"])
      .optional()
      .default("true"),
    NEXT_PUBLIC_AGENTICA_BASE_URL: z.string().optional().default("/agentica"),
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().optional(),
  },

  /**
   * Manual destructuring of process.env required for Next.js edge runtimes
   * and client-side code.
   */
  runtimeEnv: {
    // Server
    NODE_ENV: process.env.NODE_ENV,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_API_METERS: process.env.STRIPE_API_METERS,
    UNKEY_ROOT_KEY: process.env.UNKEY_ROOT_KEY,
    UNKEY_DEV_API_ID: process.env.UNKEY_DEV_API_ID,
    STRIPE_SUBSCRIPTION_PLANS: process.env.STRIPE_SUBSCRIPTION_PLANS,
    NEXT_PUBLIC_TRACES_API_BASE_URL:
      process.env.NEXT_PUBLIC_TRACES_API_BASE_URL,
    // Client
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_SITU_BASE_URL: process.env.NEXT_PUBLIC_SITU_BASE_URL,
    NEXT_PUBLIC_HELPSCOUT_BEACON_ID:
      process.env.NEXT_PUBLIC_HELPSCOUT_BEACON_ID,
    NEXT_PUBLIC_NPM_TOKEN: process.env.NEXT_PUBLIC_NPM_TOKEN,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_GRAFANA_BASE_URL: process.env.NEXT_PUBLIC_GRAFANA_BASE_URL,
    NEXT_PUBLIC_ENABLE_LOGIN: process.env.NEXT_PUBLIC_ENABLE_LOGIN,
    NEXT_PUBLIC_AGENTICA_BASE_URL: process.env.NEXT_PUBLIC_AGENTICA_BASE_URL,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
  },

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   * Useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,

  /**
   * Treat empty strings as undefined.
   */
  emptyStringAsUndefined: true,
});
